import { getJSFormattedCurrency } from '@h/currency';
import Translation from '@s/Translation';
import { CheckoutModal } from '@v/checkout/components/CheckoutModal';
import { useMemo, useRef } from 'react';
import { DateTimeFormatters } from '@showclix-shared-intl';
import { useSelector } from 'react-redux';
import { getXssFilter } from '@showclix-shared-util/xssFilter';
import { getMainEvent } from '@b/checkout/selectors/event';

const { monthDayYearShort } = DateTimeFormatters;

const processTemplateString = (template, valuesMap) => {
  let result = template || '';
  Object.entries(valuesMap).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), () => value);
  });
  return result;
};

const simpleDecode = (str) => {
  const map = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#039;': "'" };
  return str.replace(/&amp;|&lt;|&gt;|&quot;|&#039;/g, m => map[m]);
}

const ignoreTimezone = (date) => {
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return new Date(utcDate.getFullYear(), utcDate.getMonth(), utcDate.getDate());
}

export const PaymentPlanModal = ({
  open,
  onClose,
  numberOfInstallments,
  initialInstallmentAmount = {},
  detailedTerms,
  descriptionForCustomer,
  installmentDates = [],
  currencyConfig,
}) => {
  const xssFilter = useRef(getXssFilter()).current;

  const mainEvent = useSelector(getMainEvent);

  const timezone = mainEvent?.venue?.address?.timezone || '';

  const locale = useSelector((state) => state.intl.locale);
  
  const valuesMap = useMemo(() => {
    const nextInstallmentDate = installmentDates[0];

    return {
      due_upfront: getJSFormattedCurrency(initialInstallmentAmount.amount, currencyConfig),
      next_payment_date: nextInstallmentDate ?  monthDayYearShort(locale, ignoreTimezone(new Date(nextInstallmentDate))) : "",
      number_of_installments: numberOfInstallments,
      payment_schedule: `<ul>${installmentDates.map(date => monthDayYearShort(locale,ignoreTimezone(new Date(date)))).map((date) => `<li>${date}</li>`).join('')}</ul>`,
    }
  }, [numberOfInstallments, initialInstallmentAmount, installmentDates, currencyConfig, locale, timezone]);

  const processedDescriptionForCustomer = useMemo(() => {
    return xssFilter.process(simpleDecode(processTemplateString(descriptionForCustomer, valuesMap)));
  }, [descriptionForCustomer, valuesMap]);

  const processedDetailedTerms = useMemo(() => {
    return xssFilter.process(simpleDecode(processTemplateString(detailedTerms, valuesMap)));
  }, [detailedTerms, valuesMap]);

  return (
    <CheckoutModal open={open} onClose={onClose} backdrop>
      <CheckoutModal.Header>
        <CheckoutModal.Title>
          <Translation
            interpolation={{
              count: numberOfInstallments,
            }}
          >
            {'checkout__pay_in_installments'}
          </Translation>
        </CheckoutModal.Title>
        <CheckoutModal.CloseButton />
      </CheckoutModal.Header>
      <CheckoutModal.Body>
        <div className="section">
          <h4>
            <Translation>
              {'checkout__plan_details'}
            </Translation>
          </h4>
          <div dangerouslySetInnerHTML={{ __html: processedDescriptionForCustomer }} />
        </div>
        <div className="section">
          <h4>
            <Translation>
              {'checkout__plan_terms_and_conditions'}
            </Translation>
          </h4>
          <div dangerouslySetInnerHTML={{ __html: processedDetailedTerms }} />
        </div>
      </CheckoutModal.Body>
    </CheckoutModal>
  );
};
